import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupIcon from '@mui/icons-material/Group';
import { useNavigate, useLocation } from 'react-router-dom';
import { USER_GROUP } from '../../models/types';
import { useApp } from '../../context/AppContext';
import { getApiUrl } from '../../utils/apiUrl';
import { SELECTED_APP_ID_KEY } from '../shared/SelectAppPage';

function matchStaffSearch(s, q) {
  if (!q || !q.trim()) return true;
  const t = q.trim().toLowerCase();
  const username = (s.username || '').toLowerCase();
  const fullName = (s.full_name || '').toLowerCase();
  const address = (s.address || '').toLowerCase();
  const groupsStr = Array.isArray(s.groups) ? s.groups.join(' ').toLowerCase() : '';
  return username.includes(t) || fullName.includes(t) || address.includes(t) || groupsStr.includes(t);
}

const MembersManagementPage = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const [appStaffData, setAppStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');

  const canManageMembers = useMemo(() => {
    return currentUser?.group === USER_GROUP.STAFF;
  }, [currentUser?.group]);

  const fetchAppStaff = useCallback(() => {
    const selectedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;
    if (!selectedAppId) {
      setError('ไม่พบแอปที่เลือก');
      setLoading(false);
      return;
    }
    const base = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!base) {
      setError('ไม่พบ URL API');
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    const headers = { accept: 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    setLoading(true);
    setError(null);
    axios
      .get(`${base}/${selectedAppId}/app-staff/`, { headers })
      .then(({ data }) => {
        setAppStaffData(data);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err?.message || 'โหลดข้อมูลไม่สำเร็จ');
        setAppStaffData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAppStaff();
  }, [fetchAppStaff]);

  // แสดง Snackbar เมื่อกลับจากหน้าสร้าง/แก้ไขพร้อม state
  useEffect(() => {
    const msg = location.state?.message;
    const severity = location.state?.severity || 'success';
    if (msg) {
      setSnackbar({ open: true, message: msg, severity });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.message, location.state?.severity, location.pathname, navigate]);

  const departments = useMemo(
    () => (Array.isArray(appStaffData?.departments) ? appStaffData.departments : []),
    [appStaffData]
  );
  const topLevelStaff = useMemo(
    () => (Array.isArray(appStaffData?.staff) ? appStaffData.staff : []),
    [appStaffData]
  );
  const filteredTopLevelStaff = useMemo(
    () => topLevelStaff.filter((s) => matchStaffSearch(s, filterSearch)),
    [topLevelStaff, filterSearch]
  );
  const filteredDepartments = useMemo(() => {
    if (filterDepartment) {
      const dept = departments.find((d) => String(d.id) === filterDepartment);
      if (!dept) return [];
      const staff = Array.isArray(dept.staff) ? dept.staff : [];
      return [{ ...dept, staff: staff.filter((s) => matchStaffSearch(s, filterSearch)) }];
    }
    return departments.map((dept) => ({
      ...dept,
      staff: (Array.isArray(dept.staff) ? dept.staff : []).filter((s) => matchStaffSearch(s, filterSearch)),
    }));
  }, [departments, filterDepartment, filterSearch]);

  const allFilteredStaff = useMemo(() => {
    const list = [];
    const seenIds = new Set();
    const add = (s) => {
      const id = s?.id != null ? String(s.id) : null;
      if (id && seenIds.has(id)) return;
      if (id) seenIds.add(id);
      list.push(s);
    };
    if (filterDepartment === '') {
      filteredTopLevelStaff.forEach(add);
    }
    filteredDepartments.forEach((dept) => {
      const staff = Array.isArray(dept.staff) ? dept.staff : [];
      staff.forEach(add);
    });
    return list;
  }, [filterDepartment, filteredTopLevelStaff, filteredDepartments]);

  const hasAnyStaff = topLevelStaff.length > 0 || departments.some((d) => (d.staff || []).length > 0);
  const hasFilteredResults = allFilteredStaff.length > 0;
  const showFilterBar = hasAnyStaff;

  const goToEditPage = (staff) => {
    if (staff?.id != null) navigate(`/manage-members/edit/${staff.id}`);
  };

  const goToCreatePage = () => {
    navigate('/manage-members/create');
  };

  const handleDeleteConfirm = () => {
    if (!userToDelete?.id) return;
    const url = `${getApiUrl('/api/user/users/').replace(/\/$/, '')}/${userToDelete.id}/`;
    const token = localStorage.getItem('token');
    const headers = { accept: 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    setDeleteSubmitting(true);
    setDeleteError('');
    axios
      .delete(url, { headers })
      .then(() => {
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
        fetchAppStaff();
        setSnackbar({ open: true, message: 'ลบผู้ใช้สำเร็จ', severity: 'success' });
      })
      .catch((err) => {
        const errMsg = err?.response?.data?.detail || err?.message || 'ลบผู้ใช้ไม่สำเร็จ';
        setDeleteError(errMsg);
        setSnackbar({ open: true, message: errMsg, severity: 'error' });
      })
      .finally(() => setDeleteSubmitting(false));
  };

  const getDepartmentIcon = (name) => {
    if (!name) return <GroupIcon sx={{ fontSize: 20 }} />;
    if (/ประปา|น้ำ/i.test(name)) return <WaterDropIcon sx={{ fontSize: 20, color: '#2196f3' }} />;
    if (/ไฟฟ้า/i.test(name)) return <BoltIcon sx={{ fontSize: 20, color: '#ff9800' }} />;
    return <GroupIcon sx={{ fontSize: 20, color: '#845EC2' }} />;
  };

  if (!canManageMembers) {
    return (
      <Box>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <AlertTitle sx={{ fontSize: '0.9375rem' }}>ไม่มีสิทธิ์เข้าถึง</AlertTitle>
          หน้านี้ใช้ได้เฉพาะเจ้าหน้าที่ (Staff)
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 2 }}>
            กลับ
          </Button>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 2 }}>
        <CircularProgress sx={{ color: '#845EC2' }} />
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)' }}>กำลังโหลดรายชื่อเจ้าหน้าที่...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 2 }}>
          กลับ
        </Button>
      </Box>
    );
  }

  const app = appStaffData?.app ?? {};

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.25,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ManageAccountsIcon sx={{ color: 'rgba(0,0,0,0.55)' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgba(0, 0, 0, 0.65)' }}>
            จัดการผู้ดูแล
          </Typography>
          {app?.name && (
            <Chip
              size="small"
              label={app.name}
              sx={{ borderRadius: 2, fontWeight: 600, bgcolor: 'rgba(132, 94, 194, 0.12)', color: '#6344A4', border: '1px solid rgba(132, 94, 194, 0.3)' }}
            />
          )}
          {hasAnyStaff && (
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)' }}>
              ทั้งหมด {topLevelStaff.length + departments.reduce((sum, d) => sum + (d.staff || []).length, 0)} คน
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={goToCreatePage}
            sx={{
              borderRadius: 2,
              minHeight: { xs: 44, sm: 36 },
              width: { xs: '100%', sm: 'auto' },
              bgcolor: '#845EC2',
              '&:hover': { bgcolor: '#6344A4' },
            }}
          >
            สร้างผู้ใช้งาน
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: 2,
              minHeight: { xs: 44, sm: 36 },
              width: { xs: '100%', sm: 'auto' },
              color: '#845EC2',
              borderColor: 'rgba(132, 94, 194, 0.45)',
              bgcolor: 'rgba(255, 255, 255, 0.45)',
              '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)', bgcolor: 'rgba(255, 255, 255, 0.6)' },
            }}
          >
            กลับ
          </Button>
        </Box>
      </Box>

      <Card
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(132, 94, 194, 0.18)',
          border: '1px solid rgba(132, 94, 194, 0.22)',
          bgcolor: 'rgba(132, 94, 194, 0.08)',
        }}
      >
        <CardContent sx={{ py: 1.75 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.7)', mb: 0.5 }}
          >
            คำอธิบายบทบาท
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
            1. ผู้ดูแล สามารถจัดการผู้ใช้งานระดับเจ้าหน้าที่แผนกได้ (เพิ่ม, ลบ, แก้ไข)
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
            2. เจ้าหน้าที่ สามารถรับการแจ้งเตือนผ่าน RTAF SuperApps เมื่อมีการแจ้งปัญหาเข้ามาในแผนกของตนเองเท่านั้น
          </Typography>
        </CardContent>
      </Card>

      {showFilterBar && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            minWidth: 0,
            mb: 2,
            justifyContent: { xs: 'stretch', sm: 'flex-start' },
          }}
        >
          <TextField
            size="small"
            placeholder="ค้นหา ชื่อผู้ใช้, ชื่อ-นามสกุล, กลุ่ม, ที่อยู่..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            fullWidth={isXs}
            sx={{
              flex: '1 1 auto',
              minWidth: { xs: 0, sm: 180 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.55)',
                backdropFilter: 'blur(10px)',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                '&.Mui-focused fieldset': { borderColor: '#845EC2' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl
            size="small"
            fullWidth={isXs}
            sx={{
              minWidth: { xs: 0, sm: 140 },
              flex: { xs: '1 1 160px', sm: '0 0 auto' },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.55)',
                backdropFilter: 'blur(10px)',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                '&.Mui-focused fieldset': { borderColor: '#845EC2' },
              },
            }}
          >
            <InputLabel>แผนก</InputLabel>
            <Select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              label="แผนก"
            >
              <MenuItem value="">ทั้งหมด</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={String(dept.id)}>
                  {dept.name || `หน่วยงาน #${dept.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setFilterSearch('');
              setFilterDepartment('');
            }}
            fullWidth={isXs}
            sx={{
              borderRadius: 2,
              height: { xs: 44, sm: 40 },
              minHeight: { xs: 44, sm: 40 },
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              px: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.45)',
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(132, 94, 194, 0.35)',
              color: '#845EC2',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                borderColor: 'rgba(132, 94, 194, 0.55)',
              },
            }}
          >
            ล้าง
          </Button>
        </Box>
      )}

      {topLevelStaff.length === 0 && departments.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>ไม่พบหน่วยงานหรือเจ้าหน้าที่</Alert>
      )}

      {hasAnyStaff && !hasFilteredResults && (filterSearch || filterDepartment) && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>ไม่พบรายการที่ตรงกับตัวกรอง</Alert>
      )}

      {/* ตารางรวมผู้ดูแล + เจ้าหน้าที่แผนก */}
      {hasFilteredResults && (
        <Card
          elevation={0}
          sx={{
            mb: 2,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ManageAccountsIcon sx={{ fontSize: 20, color: '#845EC2' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'rgba(0, 0, 0, 0.7)' }}>
                รายชื่อเจ้าหน้าที่
              </Typography>
            </Box>
            {/* Table view: md+ */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(132, 94, 194, 0.08)' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.7)' }}>ชื่อผู้ใช้</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.7)' }}>ชื่อ-นามสกุล</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.7)' }}>กลุ่ม/สิทธิ์</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.7)' }}>ที่อยู่/เบอร์</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.7)' }} align="center">การดำเนินการ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allFilteredStaff.map((s, i) => (
                      <TableRow key={s.id != null ? `all-${s.id}-${i}` : `all-i-${i}`} hover>
                        <TableCell sx={{ color: 'rgba(0,0,0,0.75)' }}>{s.username || '-'}</TableCell>
                        <TableCell sx={{ color: 'rgba(0,0,0,0.75)' }}>{s.full_name || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {Array.isArray(s.groups) && s.groups.length > 0
                              ? s.groups.map((g, j) => (
                                  <Chip key={j} size="small" label={g} sx={{ borderRadius: 1, fontSize: '0.75rem', bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#2e7d32' }} />
                                ))
                              : '-'}
                            {s.is_staff && <Chip size="small" label="Staff" sx={{ borderRadius: 1, bgcolor: 'rgba(132, 94, 194, 0.15)', color: '#6344A4' }} />}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.875rem' }}>{s.address || '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => goToEditPage(s)} sx={{ color: 'rgba(249, 176, 7, 0.6)' }} title="แก้ไข">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={Number(s.id) === Number(currentUser?.id)}
                            onClick={() => { setUserToDelete(s); setDeleteError(''); setDeleteConfirmOpen(true); }}
                            title={Number(s.id) === Number(currentUser?.id) ? 'ไม่สามารถลบบัญชีที่กำลังใช้งานอยู่' : 'ลบ'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {/* Card view: xs/sm */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Grid container spacing={2}>
                {allFilteredStaff.map((s, i) => (
                  <Grid item xs={12} key={s.id != null ? `all-${s.id}-${i}` : `all-i-${i}`}>
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)', transform: 'translateY(-2px)' },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.8)', mb: 1 }}>
                          {s.full_name || s.username || '-'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', mb: 1 }}>
                          ชื่อผู้ใช้: {s.username || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {Array.isArray(s.groups) && s.groups.length > 0
                            ? s.groups.map((g, j) => (
                                <Chip key={j} size="small" label={g} sx={{ borderRadius: 1, fontSize: '0.75rem', bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#2e7d32' }} />
                              ))
                            : null}
                          {s.is_staff && <Chip size="small" label="Staff" sx={{ borderRadius: 1, bgcolor: 'rgba(132, 94, 194, 0.15)', color: '#6344A4' }} />}
                        </Box>
                        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.55)', fontSize: '0.875rem', mb: 2 }}>
                          ที่อยู่/เบอร์: {s.address || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={isXs ? <EditIcon fontSize="small" /> : undefined}
                            onClick={() => goToEditPage(s)}
                            sx={{ flex: 1, borderRadius: 2, color: 'rgba(249, 176, 7, 0.6)', borderColor: 'rgba(249, 176, 7, 0.6)', '&:hover': { borderColor: '#6344A4', bgcolor: 'rgba(132, 94, 194, 0.08)' } }}
                          >
                            {isXs ? 'แก้ไข' : <EditIcon fontSize="small" />}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={Number(s.id) === Number(currentUser?.id)}
                            startIcon={isXs ? <DeleteIcon fontSize="small" /> : undefined}
                            onClick={() => { setUserToDelete(s); setDeleteError(''); setDeleteConfirmOpen(true); }}
                            sx={{ flex: 1, borderRadius: 2 }}
                          >
                            {isXs ? 'ลบ' : <DeleteIcon fontSize="small" />}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog open={deleteConfirmOpen} onClose={() => !deleteSubmitting && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mt: 0.5 }}>
            <AlertTitle sx={{ fontSize: '0.9375rem' }}>ต้องการลบผู้ใช้นี้ใช่หรือไม่?</AlertTitle>
            {userToDelete && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {userToDelete.full_name || userToDelete.username || `#${userToDelete.id}`}
              </Typography>
            )}
            การลบไม่สามารถกู้คืนได้
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteSubmitting} sx={{ borderRadius: 1 }}>
            ยกเลิก
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteSubmitting} sx={{ borderRadius: 1 }}>
            {deleteSubmitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'ลบ'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MembersManagementPage;

